import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { API_ENDPOINTS, getCurrentBrand, BRAND_CONFIG } from './config/api';
import { isLemon8Enabled, isMembersEnabled, isXhsAiImageEnabled } from './utils/featureFlags';
import {
  Box,
  CssBaseline,
  ThemeProvider,
  createTheme,
  AppBar,
  Toolbar,
  Typography,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Tooltip,
  Fade,
  Menu,
  MenuItem,
  Chip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  Logout,
  Image as ImageIcon,
  AutoAwesome as AutoAwesomeIcon,
  PhotoCamera as PhotoCameraIcon,
  Security as SecurityIcon,
  AutoStories as AutoStoriesIcon,
  LocationOn as LocationOnIcon,
  ThumbUp as ThumbUpIcon,
  Slideshow as SlideshowIcon,
  Apps as AppsIcon,
  Article as ArticleIcon,
  Collections as CollectionsIcon,
  Redeem as RedeemIcon
} from '@mui/icons-material';

import axios from 'axios';

// Components
import DashboardOverview from './components/DashboardOverview';
import MasterDashboard from './components/MasterDashboard';
import Profile from './components/Profile';
import Login from './components/Login';
import IgImages from './components/IgImages';
import BannerImages from './components/BannerImages';
import LuckyDrawPage from './components/LuckyDrawPage';
import GoogleContentGenerator from './components/GoogleContentGenerator';
import FacebookContentGenerator from './components/FacebookContentGenerator';
import Lemon8ContentGenerator from './components/Lemon8ContentGenerator';
import XHSCombined from './components/XHSCombined';
import XHSCombinedCopy from './components/XHSCombinedCopy';
import XHSImageCreator from './components/XHSImageCreator';
import XHSAIImageGenerator from './components/XHSAIImageGenerator';
import PlatformContent from './components/PlatformContent';
import Users from './components/Users';
import Members from './components/Members';
import PageVisuals from './components/PageVisuals';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import LanguageSwitcher from './components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

// Context and Utils
import { RoleProvider, useRole } from './contexts/RoleContext';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2c3e50',
      light: '#34495e',
      dark: '#1a252f',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#3498db',
      light: '#5dade2',
      dark: '#2980b9',
      contrastText: '#ffffff',
    },
    background: {
      default: '#ecf0f1',
      paper: '#ffffff',
    },
    text: {
      primary: '#2c3e50',
      secondary: '#7f8c8d',
    },
    grey: {
      50: '#f8f9fa',
      100: '#e9ecef',
      200: '#dee2e6',
      300: '#ced4da',
      400: '#adb5bd',
      500: '#6c757d',
      600: '#495057',
      700: '#343a40',
      800: '#212529',
      900: '#1a1d20',
    },
    success: {
      main: '#27ae60',
      light: '#58d68d',
      dark: '#1e8449',
    },
    warning: {
      main: '#f39c12',
      light: '#f7dc6f',
      dark: '#d68910',
    },
    error: {
      main: '#e74c3c',
      light: '#ec7063',
      dark: '#c0392b',
    },
    info: {
      main: '#3498db',
      light: '#5dade2',
      dark: '#2980b9',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.05)',
    '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
    '0px 4px 6px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.06)',
    '0px 10px 15px rgba(0, 0, 0, 0.1), 0px 4px 6px rgba(0, 0, 0, 0.05)',
    '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
    '0px 25px 50px rgba(0, 0, 0, 0.1), 0px 10px 20px rgba(0, 0, 0, 0.04)',
    '0px 30px 60px rgba(0, 0, 0, 0.1), 0px 15px 30px rgba(0, 0, 0, 0.04)',
    '0px 35px 70px rgba(0, 0, 0, 0.1), 0px 20px 40px rgba(0, 0, 0, 0.04)',
    '0px 40px 80px rgba(0, 0, 0, 0.1), 0px 25px 50px rgba(0, 0, 0, 0.04)',
    '0px 45px 90px rgba(0, 0, 0, 0.1), 0px 30px 60px rgba(0, 0, 0, 0.04)',
    '0px 50px 100px rgba(0, 0, 0, 0.1), 0px 35px 70px rgba(0, 0, 0, 0.04)',
    '0px 55px 110px rgba(0, 0, 0, 0.1), 0px 40px 80px rgba(0, 0, 0, 0.04)',
    '0px 60px 120px rgba(0, 0, 0, 0.1), 0px 45px 90px rgba(0, 0, 0, 0.04)',
    '0px 65px 130px rgba(0, 0, 0, 0.1), 0px 50px 100px rgba(0, 0, 0, 0.04)',
    '0px 70px 140px rgba(0, 0, 0, 0.1), 0px 55px 110px rgba(0, 0, 0, 0.04)',
    '0px 75px 150px rgba(0, 0, 0, 0.1), 0px 60px 120px rgba(0, 0, 0, 0.04)',
    '0px 80px 160px rgba(0, 0, 0, 0.1), 0px 65px 130px rgba(0, 0, 0, 0.04)',
    '0px 85px 170px rgba(0, 0, 0, 0.1), 0px 70px 140px rgba(0, 0, 0, 0.04)',
    '0px 90px 180px rgba(0, 0, 0, 0.1), 0px 75px 150px rgba(0, 0, 0, 0.04)',
    '0px 95px 190px rgba(0, 0, 0, 0.1), 0px 80px 160px rgba(0, 0, 0, 0.04)',
    '0px 100px 200px rgba(0, 0, 0, 0.1), 0px 85px 170px rgba(0, 0, 0, 0.04)',
    '0px 105px 210px rgba(0, 0, 0, 0.1), 0px 90px 180px rgba(0, 0, 0, 0.04)',
    '0px 110px 220px rgba(0, 0, 0, 0.1), 0px 95px 190px rgba(0, 0, 0, 0.04)',
    '0px 115px 230px rgba(0, 0, 0, 0.1), 0px 100px 200px rgba(0, 0, 0, 0.04)',
    '0px 120px 240px rgba(0, 0, 0, 0.1), 0px 105px 210px rgba(0, 0, 0, 0.04)',
  ],
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#2c3e50',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
          borderBottom: '1px solid #ecf0f1',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          margin: 0,
          padding: 0,
        },
        paper: {
          backgroundColor: '#2c3e50',
          color: '#ffffff',
          borderRight: 'none',
          boxShadow: 'none',
          margin: 0,
          padding: 0,
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
          border: '1px solid #ecf0f1',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
          border: '1px solid #ecf0f1',
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 500,
        },
      },
    },
  },
});

const drawerWidth = 260;
const mobileDrawerWidth = 280;

// Main App Content Component
const MainAppContent = memo(({ onLogout, onLoginSuccess }) => {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [stats, setStats] = useState([]);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  // Set default date range to one week (GMT+8)
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
    return oneWeekAgo;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const xhsAiImageRouteEnabled = isXhsAiImageEnabled();

  // Get current page from URL
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return 'dashboard';
    if (path === '/master-dashboard') return 'master-dashboard';
    if (path === '/xhs-ai-image') return 'xhs-ai-image';
    if (path === '/xhs' || path === '/xhs-images' || path === '/xhs-content-generator') return 'xhs';
    if (path === '/xhs-copy') return 'xhs-copy';
    if (path === '/instagram-images') return 'ig-images';
    if (path === '/banner-images') return 'banner-images';
    if (path === '/lucky-draw') return 'lucky-draw';
    if (path === '/members') return 'members';
    if (path === '/page-visuals') return 'page-visuals';
    if (path === '/google-content-generator') return 'google-content-generator';
    if (path === '/google-content-generator') return 'google-content-generator';
    if (path === '/facebook-content-generator') return 'facebook-content-generator';
    if (path === '/lemon8-content-generator') return 'lemon8-content-generator';
    if (path === '/platform-content') return 'platform-content';
    if (path === '/users') return 'users';
    if (path === '/profile') return 'profile';
    return 'dashboard';
  };

  const currentPage = getCurrentPage();

  // Icon mapping function
  const getIconComponent = (iconName) => {
    const iconMap = {
      Dashboard: DashboardIcon,
      Apps: AppsIcon,
      Image: ImageIcon,
      PhotoCamera: PhotoCameraIcon,
      AutoAwesome: AutoAwesomeIcon,
      People: PeopleIcon,
      Person: PersonIcon,
      AutoStories: AutoStoriesIcon,
      LocationOn: LocationOnIcon,
      ThumbUp: ThumbUpIcon,
      Slideshow: SlideshowIcon,
      Redeem: RedeemIcon,
      Article: ArticleIcon,
      Collections: CollectionsIcon,
      Description: ArticleIcon
    };
    return iconMap[iconName] || DashboardIcon;
  };

  // DrawerContent component defined inside App to access state
  const DrawerContent = memo(() => {
    const { getAvailableRoutes, getRoleDisplayInfo } = useRole();
    const { t } = useTranslation();
    const availableRoutes = getAvailableRoutes();
    const roleInfo = getRoleDisplayInfo();

    return (
      <Box sx={{
        height: '100vh',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        margin: 0,
        padding: 0,
        backgroundColor: '#2c3e50',
        width: drawerWidth,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Logo Section */}
        <Box sx={{ p: 3, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: '#3498db', width: 40, height: 40 }}>
              <DashboardIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                {BRAND_CONFIG.displayName}
              </Typography>
              {roleInfo && (
                <Chip
                  label={roleInfo.name}
                  size="small"
                  icon={<SecurityIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    backgroundColor: 'rgba(52, 152, 219, 0.2)',
                    color: 'white',
                    fontSize: '0.7rem',
                    height: 20,
                    '& .MuiChip-icon': {
                      color: 'white'
                    }
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Navigation Menu */}
        <Box sx={{ flex: 1, py: 2, overflow: 'auto' }}>
          <List sx={{ px: 1, height: '100%' }}>
            {availableRoutes.map((route) => {
              const IconComponent = getIconComponent(route.icon);
              const isActive = currentPage === route.id ||
                (route.id === 'xhs' && (currentPage === 'xhs' || currentPage === 'store-images' || currentPage === 'xhs-content-generator')) ||
                (route.id === 'instagram-images' && currentPage === 'ig-images');

              return (
                <ListItem
                  key={route.id}
                  component="button"
                  onClick={() => navigate(route.path)}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    backgroundColor: isActive ? '#3498db' : 'transparent',
                    color: isActive ? 'white' : 'rgba(255,255,255,0.8)',
                    py: 1.5,
                    border: 'none',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: isActive ? '#2980b9' : 'rgba(255,255,255,0.1)',
                      boxShadow: 'none',
                      transform: 'none'
                    },
                    '&:active': {
                      boxShadow: 'none',
                      transform: 'none'
                    },
                    '&:focus': {
                      boxShadow: 'none',
                      outline: 'none'
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                    <IconComponent sx={{ fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(`nav.${route.id}`, { defaultValue: route.label })}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '1rem' }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Box>
    );
  });

  // Use ref to track if a request is in progress to prevent duplicate calls (e.g., from React Strict Mode)
  const loadingRef = useRef(false);
  const abortControllerRef = useRef(null);

  const loadStats = useCallback(async () => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Check if we're already loading
    if (loadingRef.current) {
      return; // Skip if already loading
    }

    // Use Malaysia timezone date format (YYYY-MM-DD) to match DashboardOverview
    const startDateStr = startDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' });
    const endDateStr = endDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' });

    loadingRef.current = true;
    setIsLoading(true);

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const response = await axios.get(API_ENDPOINTS.ANALYTICS_SUMMARY, {
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
          brand: getCurrentBrand()
        },
        signal: abortController.signal
      });

      // Only update state if request wasn't aborted
      if (!abortController.signal.aborted) {
        setStats(response.data);
      }
    } catch (error) {
      // Ignore abort errors
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }
      console.error('Error loading stats:', error);
      // Set empty array as fallback (matching the API response format)
      if (!abortController.signal.aborted) {
        setStats([]);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false);
        loadingRef.current = false;
        abortControllerRef.current = null;
      }
    }
  }, [startDate, endDate]);

  // Load initial stats when component mounts or dates change
  useEffect(() => {
    loadStats();

    // Cleanup: cancel any in-flight request when effect re-runs or component unmounts
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      loadingRef.current = false;
    };
  }, [loadStats]);

  const handleRefresh = useCallback(() => {
    loadStats();
  }, [loadStats]);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(!mobileOpen);
  }, [mobileOpen]);

  const handleUserMenuOpen = useCallback((event) => {
    setUserMenuAnchor(event.currentTarget);
  }, []);

  const handleUserMenuClose = useCallback(() => {
    setUserMenuAnchor(null);
  }, []);

  const handleDataUpdate = useCallback((chartData) => {
    // You can use this to sync chart data with stats if needed
    console.log('Chart data updated:', chartData);
  }, []);

  const handleDateChangeCallback = useCallback((newStartDate, newEndDate) => {
    // Just update the dates - the useEffect with loadStats dependency will handle the API call
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  }, []);

  const handleLogout = useCallback(() => {
    handleUserMenuClose();
    onLogout();
  }, [handleUserMenuClose, onLogout]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', margin: 0, padding: 0 }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { xl: `calc(100% - ${drawerWidth}px)` },
          ml: { xl: `${drawerWidth}px` },
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}
      >
        <Toolbar sx={{
          px: { xs: 2, sm: 3 },
          minHeight: { xs: 56, sm: 64 },
          justifyContent: 'space-between'
        }}>
          <Box display="flex" alignItems="center">
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                mr: { xs: 1, sm: 2 },
                display: { xl: 'none' },
                color: '#2c3e50'
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                flexGrow: 1,
                fontWeight: 700,
                color: '#2c3e50',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                display: { xs: 'none', sm: 'block' }
              }}
            >
              {currentPage === 'dashboard' ? t('app.title.dashboard') :
                currentPage === 'master-dashboard' ? t('masterDashboard.title', { defaultValue: 'Master Dashboard' }) :
                  currentPage === 'xhs-ai-image' ? t('app.title.xhsAiImage', { defaultValue: 'AI Image Generator' }) :
                  currentPage === 'xhs' ? 'XHS Content Generator' :
                    currentPage === 'ig-images' ? t('app.title.ig') :
                      currentPage === 'lucky-draw' ? t('app.title.luckyDraw', { defaultValue: 'Lucky Draw' }) :
                      currentPage === 'members' ? 'Members' :
                      currentPage === 'page-visuals' ? 'Page Visuals' :
                      currentPage === 'google-content-generator' ? 'Google Content Generator' :
                        currentPage === 'facebook-content-generator' ? 'Facebook Content Generator' :
                            currentPage === 'lemon8-content-generator' ? 'Lemon8 Content Generator' :
                          currentPage === 'users' ? t('app.title.users') :
                            currentPage === 'profile' ? t('app.title.profile') :
                              t('app.title.dashboard')}
            </Typography>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                flexGrow: 1,
                fontWeight: 700,
                color: '#2c3e50',
                fontSize: '1rem',
                display: { xs: 'block', sm: 'none' }
              }}
            >
              {currentPage === 'dashboard' ? t('app.title.dashboardShort') :
                currentPage === 'master-dashboard' ? t('masterDashboard.title', { defaultValue: 'Master' }) :
                  currentPage === 'xhs-ai-image' ? t('app.title.xhsAiImageShort', { defaultValue: 'AI Image' }) :
                  currentPage === 'xhs' ? 'XHS Content Generator' :
                    currentPage === 'ig-images' ? t('app.title.igShort') :
                      currentPage === 'lucky-draw' ? t('app.title.luckyDrawShort', { defaultValue: 'Lucky Draw' }) :
                      currentPage === 'members' ? 'Members' :
                      currentPage === 'page-visuals' ? 'Page Visuals' :
                      currentPage === 'google-content-generator' ? 'Google' :
                        currentPage === 'facebook-content-generator' ? 'Facebook' :
                          currentPage === 'lemon8-content-generator' ? 'Lemon8' :
                          currentPage === 'users' ? t('app.title.usersShort') :
                            currentPage === 'profile' ? t('app.title.profileShort') : t('app.title.dashboardShort')}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <LanguageSwitcher />
            <Tooltip title={t('app.userMenu')}>
              <IconButton
                color="inherit"
                onClick={handleUserMenuOpen}
                sx={{
                  color: '#2c3e50',
                  '&:hover': {
                    backgroundColor: 'rgba(52, 152, 219, 0.1)',
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <Avatar
                  sx={{
                    width: { xs: 32, sm: 36 },
                    height: { xs: 32, sm: 36 },
                    bgcolor: '#3498db',
                    border: '2px solid rgba(52, 152, 219, 0.2)',
                    boxShadow: '0 4px 12px rgba(52, 152, 219, 0.3)'
                  }}
                >
                  <PersonIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)',
            borderRadius: 2,
            border: '1px solid rgba(0,0,0,0.06)',
            background: '#ffffff',
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >

        <MenuItem
          onClick={handleLogout}
          sx={{
            py: 2,
            px: 2.5,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: '#f8f9fa',
              '& .MuiListItemIcon-root': {
                color: '#e74c3c'
              }
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Logout sx={{ color: '#6c757d', fontSize: 20 }} />
          </ListItemIcon>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#2c3e50' }}>
            {t('app.logout')}
          </Typography>
        </MenuItem>
      </Menu>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: { xl: drawerWidth },
          flexShrink: { xl: 0 },
          margin: 0,
          padding: 0
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', xl: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: { xs: mobileDrawerWidth, sm: drawerWidth },
              margin: 0,
              padding: 0,
              height: '100vh',
              maxHeight: '100vh',
              top: 0,
              left: 0,
              position: 'fixed',
              overflow: 'hidden'
            },
          }}
        >
          <DrawerContent />
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', xl: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              margin: 0,
              padding: 0,
              height: '100%',
              maxHeight: '100%',
              top: 0,
              left: 0,
              position: 'fixed'
            },
          }}
          open
        >
          <DrawerContent />
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { xl: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: 'background.default',
          minHeight: '100vh',
          margin: 0,
          padding: 0,
          overflow: 'visible',
          position: 'relative'
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            px: 0,
            mx: 0,
            width: '100%',
            overflow: 'visible',
            position: 'relative'
          }}
        >
          <Fade in timeout={500}>
            <Box sx={{
              pt: { xs: 8, sm: 10, md: 12, lg: 10, xl: 10 },
              px: { xs: 1, sm: 2, md: 3, lg: 3, xl: 4 },
              overflow: 'visible',
              position: 'relative',
              minHeight: 'calc(100vh - 64px)'
            }}>

              {/* React Router Routes */}
              <Routes>
                <Route path="/login" element={<Navigate to="/dashboard" replace />} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={
                  <RoleProtectedRoute requiredPermission="dashboard">
                    <DashboardOverview
                      stats={stats}
                      startDate={startDate}
                      endDate={endDate}
                      onDataUpdate={handleDataUpdate}
                      onDateChange={handleDateChangeCallback}
                      onRefresh={handleRefresh}
                      isLoading={isLoading}
                    />
                  </RoleProtectedRoute>
                } />
                <Route path="/master-dashboard" element={
                  <RoleProtectedRoute requiredPermission="master-dashboard">
                    <MasterDashboard />
                  </RoleProtectedRoute>
                } />
                <Route path="/xhs" element={
                  <RoleProtectedRoute requiredPermission="xhs-content-generator">
                    <XHSCombined />
                  </RoleProtectedRoute>
                } />
                <Route path="/xhs-ai-image" element={
                  xhsAiImageRouteEnabled ? (
                    <RoleProtectedRoute requiredPermission="xhs-ai-image">
                      <XHSAIImageGenerator />
                    </RoleProtectedRoute>
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                } />
                <Route path="/xhs-copy" element={
                  <RoleProtectedRoute requiredPermission="xhs-content-generator-copy">
                    <XHSCombinedCopy />
                  </RoleProtectedRoute>
                } />
                <Route path="/platform-content" element={
                  <RoleProtectedRoute requiredPermission="platform-content">
                    <PlatformContent />
                  </RoleProtectedRoute>
                } />
                <Route path="/xhs-images" element={
                  <Navigate to="/xhs#images" replace />
                } />
                <Route path="/xhs-content-generator" element={
                  <Navigate to="/xhs#generator" replace />
                } />
                <Route path="/xhs-image-creator" element={
                  <RoleProtectedRoute requiredPermission="xhs-content-generator">
                    <XHSImageCreator />
                  </RoleProtectedRoute>
                } />
                <Route path="/instagram-images" element={
                  <RoleProtectedRoute requiredPermission="instagram-images">
                    <IgImages />
                  </RoleProtectedRoute>
                } />
                <Route path="/vehicle-models" element={<Navigate to="/xhs#models" replace />} />
                <Route path="/banner-images" element={
                  <RoleProtectedRoute requiredPermission="banner-images">
                    <BannerImages />
                  </RoleProtectedRoute>
                } />
                <Route path="/lucky-draw" element={
                  <RoleProtectedRoute requiredPermission="lucky-draw">
                    <LuckyDrawPage />
                  </RoleProtectedRoute>
                } />
                <Route path="/members" element={
                  isMembersEnabled() ? (
                    <RoleProtectedRoute requiredPermission="lucky-draw">
                      <Members />
                    </RoleProtectedRoute>
                  ) : <Navigate to="/dashboard" replace />
                } />
                <Route path="/page-visuals" element={
                  isMembersEnabled() ? <RoleProtectedRoute requiredPermission="platform-content"><PageVisuals /></RoleProtectedRoute> : <Navigate to="/dashboard" replace />
                } />
                <Route path="/google-content-generator" element={
                  <RoleProtectedRoute requiredPermission="google-content-generator">
                    <GoogleContentGenerator />
                  </RoleProtectedRoute>
                } />
                <Route path="/facebook-content-generator" element={
                  <RoleProtectedRoute requiredPermission="facebook-content-generator">
                    <FacebookContentGenerator />
                  </RoleProtectedRoute>
                } />
                <Route path="/lemon8-content-generator" element={
                  <RoleProtectedRoute requiredPermission="lemon8-content-generator">
                    {isLemon8Enabled() ? <Lemon8ContentGenerator /> : <Navigate to="/dashboard" replace />}
                  </RoleProtectedRoute>
                } />
                <Route path="/users" element={
                  <RoleProtectedRoute requiredPermission="users">
                    <Users />
                  </RoleProtectedRoute>
                } />
                <Route path="/profile" element={
                  <RoleProtectedRoute requiredPermission="profile">
                    <Profile onBackToDashboard={() => navigate('/dashboard')} />
                  </RoleProtectedRoute>
                } />
              </Routes>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
});

// Main App Component with Router
const App = memo(() => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Authentication functions
  const handleLoginSuccess = useCallback(() => {
    setIsAuthenticated(true);
    setIsCheckingAuth(false);
    // Dispatch custom event to refresh RoleContext
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('userDataUpdated'));

      // Check user role and redirect accordingly
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        try {
          const userData = JSON.parse(storedUserData);
          const userRole = userData.role_name || userData.role_id;

          // If user is ADMIN (intern) and doesn't have dashboard permission, redirect to Google Content Generator
          if (userRole === 'admin' || userRole === 1) {
            window.location.href = '/google-content-generator';
          } else {
            // For other roles, redirect to dashboard
            window.location.href = '/dashboard';
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
          // Fallback to dashboard
          window.location.href = '/dashboard';
        }
      } else {
        // Fallback to dashboard if no user data
        window.location.href = '/dashboard';
      }
    }, 100); // Small delay to ensure localStorage is updated
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userData');
    localStorage.removeItem('username');
    setIsAuthenticated(false);
    setIsCheckingAuth(false);
    // Dispatch custom event to clear RoleContext
    window.dispatchEvent(new CustomEvent('userLoggedOut'));
    // Navigate to root path and clear browser history
    window.history.replaceState(null, '', '/');
    // Force a page reload to ensure clean state
    window.location.reload();
  }, []);

  // Check authentication status on component mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      setIsAuthenticated(true);
    }
    setIsCheckingAuth(false);
  }, []);

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
          }}
        >
          <Typography variant="h6" sx={{ color: 'white' }}>
            Loading...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <RoleProvider>
          {!isAuthenticated ? (
            // Login Page
            <Login onLoginSuccess={handleLoginSuccess} />
          ) : (
            // Main App with Routing
            <MainAppContent onLogout={handleLogout} onLoginSuccess={handleLoginSuccess} />
          )}
        </RoleProvider>
      </Router>
    </ThemeProvider>
  );
});

export default App;
